import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import ThumbnailImage from "../../../core/common/thumbnailImage";
import CustomModal from "../../../core/common/customModal";
import { Subscription, SubscriptionDetailsModalProps } from "../../../core/data/interface";
import { all_routes } from "../../router/all_routes";
import "./subscriptions.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { DEFAULT_THUMBNAIL_URL } from "../../../environment";

// Extended props interface to include enrollment status
interface ExtendedSubscriptionDetailsModalProps extends SubscriptionDetailsModalProps {
  isEnrolled?: boolean;
}
// Helper function to calculate discount percentage
const calculateDiscountPercentage = (originalPrice: number, promotionalPrice: number) => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - promotionalPrice) / originalPrice) * 100);
};

// Helper function to format subscription type for display
const formatSubscriptionType = (planType: string) => {
  return planType.charAt(0).toUpperCase() + planType.slice(1).toLowerCase();
};

// Helper function to get unique course thumbnails for carousel
const getCourseThumbnails = (subscription: Subscription) => {
  return subscription.courses.map(course => ({
    courseId: course.courseId,
    courseTitle: course.courseTitle,
    thumbnail: course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL
  }));
};

// Helper function to count study materials for a single course
const countCourseStudyMaterials = (course: any) => {
  let videos = 0;
  let documents = 0;
  let links = 0;
  let tests = 0;
  
  course?.courseDetails?.subjects?.forEach((subject: any) => {
    subject?.syllabus?.forEach((syllabus: any) => {
      syllabus.studyMaterials.forEach((material: any) => {
        switch (material.materialType) {
          case 'VIDEO':
            videos++;
            break;
          case 'NOTE':
          case 'FILE':
            documents++;
            break;
          case 'LINK':
            // Check if it's a YouTube link (including YouTube Shorts)
            if (material.content && (material.content.includes('youtube.com') || material.content.includes('youtu.be') || material.content.includes('m.youtube.com'))) {
              videos++;
            } else {
              links++;
            }
            break;
        }
      });
    });
  });
  
  return { videos, documents, links, tests };
};

// Helper function to render stars for rating
const renderStars = (rating: number) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  for (let i = 0; i < fullStars; i++) {
    stars.push(<i key={i} className="fas fa-star text-warning"></i>);
  }

  if (hasHalfStar) {
    stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
  }

  const emptyStars = 5 - Math.ceil(rating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<i key={`empty-${i}`} className="far fa-star text-warning"></i>);
  }

  return stars;
};

const SubscriptionDetailsModal: React.FC<ExtendedSubscriptionDetailsModalProps> = ({ subscription, onClose, isEnrolled = false }) => {
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  
  // Set first course as selected by default
  React.useEffect(() => {
    if (subscription && subscription.courses.length > 0) {
      setSelectedCourse(subscription.courses[0]);
    }
  }, [subscription]);
  
  if (!subscription) return null;

  const handleSubscribe = (subscription: Subscription) => {
    onClose();
    navigate(all_routes.order + '?subscriptionPlanId=' + subscription.subscriptionPlanId);
  };

  const discountPercentage = calculateDiscountPercentage(subscription.pricing.price, subscription.pricing.promotionalPrice);
  const thumbnails = getCourseThumbnails(subscription);

  // Carousel settings for course thumbnails
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    adaptiveHeight: true
  };

  const bodyContent = (
    <div className="row">
      {/* Subscription Image and Basic Info */}
      <div className="col-lg-5">
        <div className="position-relative mb-4">
          {thumbnails?.length > 1 ? (
            <div className="rounded overflow-hidden subscription-details-modal">
              <Slider {...carouselSettings}>
                {thumbnails.map((thumbnail, index) => (
                  <div key={index}>
                    <ThumbnailImage
                      src={thumbnail.thumbnail}
                      alt={thumbnail.courseTitle}
                      className="subscription-details-image"
                    />
                  </div>
                ))}
              </Slider>
            </div>
          ) : (
            <div className="rounded overflow-hidden">
              <ThumbnailImage
                src={thumbnails[0]?.thumbnail || DEFAULT_THUMBNAIL_URL}
                alt={subscription.planName}
                className="subscription-details-image"
              />
            </div>
          )}
          <div className="position-absolute top-0 start-0 p-2" style={{ zIndex: 10 }}>
            <span className="badge bg-primary">{formatSubscriptionType(subscription.planType)}</span>
          </div>
        </div>

        {/* Subscription Plan Details */}
        <div className="mb-4">
          <h3 className="mb-2">{subscription.planName}</h3>
        
        {/* Description */}
        <div className="mb-4">
          <p className="text-muted">{subscription.planDescription}</p>
        </div>
          <div className="mb-3">
            <div className="p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div className="fw-medium">{subscription.duration ? `${subscription.duration} Months` : 'Lifetime'}</div>
                  <div className="small text-muted">Access Duration</div>
                </div>
                <div className="text-end">
                  <div className="d-flex align-items-center">
                    {subscription.pricing.price > 0 && (
                      <span className="text-decoration-line-through text-muted me-2 small">
                        {subscription.pricing.currencyCode} {subscription.pricing.price}
                      </span>
                    )}
                    <span className="h5 fw-bold text-primary mb-0">
                      {subscription.pricing.currencyCode} {subscription.pricing.promotionalPrice}
                    </span>
                  </div>
                  {discountPercentage > 0 && (
                    <div className="small text-success">
                      {discountPercentage}% OFF
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Subscribe Button */}
            <div className="d-grid mt-3">
              {isEnrolled ? (
                <div className="btn btn-success disabled">
                  <i className="ti ti-check me-2"></i>
                  Enrolled
                </div>
              ) : (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleSubscribe(subscription)}
                >
                  <i className="ti ti-shopping-cart me-2"></i>
                  Enroll Now
                </button>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Subscription Details */}
      <div className="col-lg-7">

        {/* Included Courses */}
        {subscription?.courses?.length > 1 && (
          <div className="mb-4">
            <h4>Courses</h4>
            <div className="row g-2 mt-1">
              {subscription.courses.map(course => (
                <div key={course.courseId} className="col-md-6">
                  <div 
                    className={`p-3 border rounded cursor-pointer ${selectedCourse?.courseId === course.courseId ? 'border-primary bg-light' : ''}`}
                    onClick={() => setSelectedCourse(course)}
                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                  >
                    <div className="d-flex align-items-center">
                      <ImageWithBasePath
                        src={course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL}
                        alt={course.courseTitle}
                        className="me-3 rounded"
                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                      />
                      <div>
                        <div className="fw-medium">{course.courseTitle}</div>
                        <small className="text-muted">{course.courseType}</small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Course Details */}
        {selectedCourse && (
          <div className="mb-4">
            <div className="border rounded p-3 bg-light">
              <h5 className="mb-3">{selectedCourse.courseTitle}</h5>
              
              {/* Rating and Analytics */}
              <div className="d-flex align-items-center mb-3">
                <div className="me-3">
                  {renderStars(selectedCourse.analytics.averageRating)}
                </div>
                <span className="fw-medium me-3">{selectedCourse.analytics.averageRating.toFixed(1)}</span>
                <span className="text-muted">({selectedCourse.analytics.totalReviews} reviews)</span>
              </div>

              {/* Description */}
              <div className="mb-3">
                <h6>Description</h6>
                <p className="text-muted small">{selectedCourse.courseDescription}</p>
              </div>

              {/* Course Materials Details */}
              <div className="mb-3">
                <h6 className="mb-2">Course Materials</h6>
                <div className="row g-2">

                  <div className="col-md-4">
                    <div className="position-relative p-3 bg-white rounded border">
                        <div className="d-flex align-items-center">
                        <i className="ti ti-video text-primary me-2" style={{fontSize: '20px'}}></i>
                        <span className="fw-medium">{countCourseStudyMaterials(selectedCourse).videos} Video Lessons</span>
                        </div>
                        <div className="small text-muted">Comprehensive video tutorials covering all topics</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="position-relative p-3 bg-white rounded border">
                        <div className="d-flex align-items-center">
                        <i className="ti ti-file-text text-info me-2" style={{fontSize: '20px'}}></i>
                        <span className="fw-medium">{countCourseStudyMaterials(selectedCourse).documents + countCourseStudyMaterials(selectedCourse).links} Study Materials</span>
                        </div>
                        <div className="small text-muted">PDFs, guides, and reference documents</div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="position-relative p-3 bg-white rounded border">
                        <div className="d-flex align-items-center">
                        <i className="ti ti-clipboard-check text-success me-2" style={{fontSize: '20px'}}></i>
                        <span className="fw-medium">{selectedCourse.assessmentCount || 0} Tests</span>
                        </div>
                        <div className="small text-muted">Interactive assessments and quizzes</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Subjects */}
              {selectedCourse?.courseDetails?.subjects?.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">Course Subjects</h6>
                  <div className="row g-1">
                    {selectedCourse.courseDetails.subjects.map((subject: any) => (
                      <div key={subject?.subjectId} className="col-md-6">
                        <div className="p-2 border rounded bg-white">
                          <div className="fw-medium small">{subject?.subjectTitle}</div>
                          <small className="text-muted">{subject?.subjectDescription}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What You'll Learn */}
              {selectedCourse?.courseMetadata?.learning_highlights?.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">What You'll Learn</h6>
                  <ul className="list-unstyled">
                    {selectedCourse.courseMetadata?.learning_highlights?.map((highlight: string, index: number) => (
                      <li key={index} className="mb-1">
                        <i className="ti ti-check text-success me-2" style={{fontSize: '12px'}}></i>
                        <small>{highlight}</small>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );

  const footerContent = (
    <div className="d-flex justify-content-end w-100">
      <button
        type="button"
        className="btn btn-light"
        onClick={onClose}
      >
        Close
      </button>
    </div>
  );

  return (
    <CustomModal
      show={!!subscription}
      title={subscription.planName || "Batch Details"}
      onClose={onClose}
      bodyContent={bodyContent}
      footerContent={footerContent}
      size="xl"
      backdrop="static"
    />
  );
};

export default SubscriptionDetailsModal;
